'use client'

/**
 * Desk Pulse — branded, customer-selective snapshot bay in the client rail.
 *
 * Free: one Capital widget. Paid unlocks more slots and Book / Agents.
 * Selection persists locally; locked options route to plan settings.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Popover, Tooltip } from 'antd'
import { LockOutlined } from '@ant-design/icons'
import { DeskReadout } from '@/shared/components/desk'
import { useRouter } from 'next/navigation'
import { usePlanEntitlements } from '@/hooks/use-plan-entitlements'
import { usePortfolioOverview, useOrders } from '@/domains/trading/hooks/useTrading'
import { useAgents } from '@/domains/ai-trading/hooks/useAgents'
import { useOptionalClientDeskSession } from '@/shared/providers/context/ClientDeskSessionContext'
import { formatPct, formatCount } from '@/app/client/dashboard/flagship/formatInstrument'
import { formatCurrency } from '@/shared/utils/formatters'
import {
  PULSE_WIDGETS,
  type PulseWidgetId,
  canUsePulseWidget,
  normalizePulseSelection,
  pulseSlotCount,
  readPulseSelection,
  unlockLabel,
  writePulseSelection,
} from './pulseWidgetCatalog'
import { DESK_STATUS } from '@/app/client/dashboard/flagship/deskStatus'
import styles from './Sidebar.module.css'

export type PulseWidgetHolderProps = {
  collapsed: boolean
  headerOnly?: boolean
}

const PRODUCT_NAME = 'Desk Pulse'
const PRODUCT_TAG = 'Snapshots'

/** Narrow-rail money: full value in title, compact on face when long. */
function railMoney(value: number | null | undefined, ready: boolean): string {
  if (!ready || value == null || Number.isNaN(value)) return '—'
  if (Math.abs(value) >= 10_000) {
    return formatCurrency(value, 'USD', { compact: true })
  }
  return formatCurrency(value)
}

function isWorkingOrder(status: unknown): boolean {
  const s = String(status ?? '').toLowerCase()
  return (
    s === 'open' ||
    s === 'working' ||
    s === 'new' ||
    s === 'partially_filled' ||
    s === 'accepted' ||
    s === 'pending' ||
    s === 'submitted'
  )
}

function finiteNum(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  return value
}

function DeskPulseMark({ live }: { live?: boolean }) {
  return (
    <span
      className={`${styles.pulseMark} ${live ? styles.pulseMarkLive : ''}`}
      aria-hidden
    >
      <span className={styles.pulseMarkCore} />
      <span className={styles.pulseMarkRing} />
    </span>
  )
}

function CapitalWidget({ onOpen }: { onOpen: () => void }) {
  const desk = useOptionalClientDeskSession()
  const { data, isLoading, isError } = usePortfolioOverview()
  const balances = desk?.workspace?.balances
  const deskReady =
    desk?.workspace?.sections?.balances?.status === 'ready' && Boolean(balances)
  const apiReady = Boolean(data) && !isError
  const ready = deskReady || apiReady
  const equity = deskReady
    ? finiteNum(balances?.equity)
    : finiteNum(data?.total_value)
  const dayPnl = deskReady ? null : finiteNum(data?.day_pnl)
  const dayPct = deskReady ? null : finiteNum(data?.day_pnl_percent)
  const pnlUp = ready && dayPnl != null && dayPnl > 0
  const pnlDown = ready && dayPnl != null && dayPnl < 0
  const loading = Boolean(desk?.workspaceLoading) || (isLoading && !ready)

  return (
    <button
      type="button"
      className={`${styles.widgetCard} ${styles.widgetCardLead}`}
      onClick={onOpen}
      aria-label="Capital snapshot"
    >
      <div className={styles.widgetBand}>
        <span className={styles.widgetBandLabel}>Capital</span>
        <span className={styles.widgetBandMeta}>
          {loading
            ? DESK_STATUS.loading
            : !ready
              ? DESK_STATUS.unavailable
              : DESK_STATUS.updated}
        </span>
      </div>
      <div className={styles.widgetBody}>
        <DeskReadout
          label="Equity"
          value={railMoney(equity, ready)}
          empty={!ready || equity == null}
          emphasis="lead"
        />
        <DeskReadout
          label="Day P&L"
          value={
            dayPnl == null && deskReady
              ? '—'
              : `${railMoney(dayPnl, ready && dayPnl != null)}${
                  ready && dayPct != null ? ` ${formatPct(dayPct, true)}` : ''
                }`
          }
          empty={dayPnl == null}
          tone={pnlUp ? 'gain' : pnlDown ? 'loss' : 'neutral'}
        />
      </div>
    </button>
  )
}

function BookWidget({ onOpen }: { onOpen: () => void }) {
  const desk = useOptionalClientDeskSession()
  const { data: overview, isLoading: overviewLoading, isError: overviewError } =
    usePortfolioOverview()
  const { data: orders, isLoading: ordersLoading, isError: ordersError } = useOrders()

  const deskPosReady = desk?.workspace?.sections?.positions?.status === 'ready'
  const deskOrdReady = desk?.workspace?.sections?.orders?.status === 'ready'
  const posReady = deskPosReady || (Boolean(overview) && !overviewError)
  const ordReady =
    deskOrdReady || (Array.isArray(orders) && !ordersError)

  const positions = deskPosReady
    ? desk?.workspace?.positions?.length ?? 0
    : overview?.positions_count ?? null

  const working = deskOrdReady
    ? (desk?.workspace?.orders || []).filter((o) => isWorkingOrder(o.status)).length
    : ordReady
      ? (orders as Array<{ status?: string }>).filter((o) => isWorkingOrder(o.status))
          .length
      : null

  return (
    <button type="button" className={styles.widgetCard} onClick={onOpen} aria-label="Book snapshot">
      <div className={styles.widgetBand}>
        <span className={styles.widgetBandLabel}>Book</span>
        <span className={styles.widgetBandMeta}>
          {(overviewLoading || ordersLoading || desk?.workspaceLoading) &&
          !(posReady || ordReady)
            ? '…'
            : 'Open'}
        </span>
      </div>
      <div className={styles.widgetBody}>
        <DeskReadout
          label="Pos"
          value={formatCount(positions, posReady)}
          empty={!posReady || positions == null}
        />
        <DeskReadout
          label="Ords"
          value={formatCount(working, ordReady)}
          empty={!ordReady || working == null}
        />
      </div>
    </button>
  )
}

function AgentsWidget({ onOpen }: { onOpen: () => void }) {
  const { counts, isLoading, error } = useAgents()
  const ready = !error && !isLoading
  return (
    <button type="button" className={styles.widgetCard} onClick={onOpen} aria-label="Agents snapshot">
      <div className={styles.widgetBand}>
        <span className={styles.widgetBandLabel}>Agents</span>
        <span className={styles.widgetBandMeta}>{isLoading ? '…' : 'Desk'}</span>
      </div>
      <div className={styles.widgetBody}>
        <DeskReadout
          label="Run"
          value={formatCount(counts.running, ready)}
          empty={!ready}
          tone={ready && counts.running > 0 ? 'gain' : 'neutral'}
        />
        <DeskReadout
          label="Psd"
          value={formatCount(counts.paused, ready)}
          empty={!ready}
        />
        <DeskReadout
          label="Err"
          value={formatCount(counts.error, ready)}
          empty={!ready}
          tone={ready && counts.error > 0 ? 'loss' : 'neutral'}
        />
      </div>
    </button>
  )
}

function LockedSlot({ label, unlock }: { label: string; unlock: string }) {
  const router = useRouter()
  return (
    <button
      type="button"
      className={`${styles.widgetCard} ${styles.widgetLocked}`}
      onClick={() => router.push('/settings')}
      aria-label={`${label} locked — ${unlock} required`}
    >
      <div className={styles.widgetBand}>
        <span className={styles.widgetBandLabel}>{label}</span>
        <LockOutlined className={styles.widgetLockIcon} aria-hidden />
      </div>
      <div className={styles.widgetBody}>
        <p className={styles.widgetLockCopy}>
          Unlock with <strong>{unlock}</strong>
        </p>
      </div>
    </button>
  )
}

function CompactPulseCard({
  selected,
  onNavigate,
}: {
  selected: PulseWidgetId[]
  onNavigate: (href: string) => void
}) {
  return (
    <div className={styles.pulseCompact} aria-label="Desk snapshots">
      {selected.includes('capital') ? (
        <CapitalWidget
          onOpen={() =>
            onNavigate(PULSE_WIDGETS.find((item) => item.id === 'capital')?.href || '/client/dashboard')
          }
        />
      ) : null}
      {selected.includes('book') ? (
        <BookWidget
          onOpen={() =>
            onNavigate(PULSE_WIDGETS.find((item) => item.id === 'book')?.href || '/portfolio')
          }
        />
      ) : null}
      {selected.includes('agents') ? (
        <AgentsWidget
          onOpen={() =>
            onNavigate(PULSE_WIDGETS.find((item) => item.id === 'agents')?.href || '/ai-trading')
          }
        />
      ) : null}
    </div>
  )
}

export function PulseWidgetHolder({ collapsed, headerOnly = false }: PulseWidgetHolderProps) {
  const router = useRouter()
  const desk = useOptionalClientDeskSession()
  const { tier, isFree, isLoading: planLoading } = usePlanEntitlements()
  const [mounted, setMounted] = useState(false)
  const [selected, setSelected] = useState<PulseWidgetId[]>(['capital'])
  const [pickerOpen, setPickerOpen] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!mounted || planLoading) return
    setSelected(readPulseSelection(tier))
  }, [mounted, planLoading, tier])

  const slots = pulseSlotCount(tier)
  const planName =
    tier === 'none' ? 'Free' : isFree ? 'Free' : tier === 'starter' ? 'Operator' : 'Desk+'
  const pathLive = Boolean(desk?.isPathConnected)

  const persist = useCallback(
    (next: PulseWidgetId[]) => {
      const normalized = normalizePulseSelection(next, tier)
      setSelected(normalized)
      writePulseSelection(normalized)
    },
    [tier]
  )

  const toggleWidget = useCallback(
    (id: PulseWidgetId) => {
      if (!canUsePulseWidget(id, tier)) {
        router.push('/settings')
        setPickerOpen(false)
        return
      }
      if (selected.includes(id)) {
        if (selected.length <= 1) return
        persist(selected.filter((x) => x !== id))
        return
      }
      if (selected.length >= slots) {
        persist([...selected.slice(1), id])
        return
      }
      persist([...selected, id])
    },
    [persist, router, selected, slots, tier]
  )

  const picker = useMemo(
    () => (
      <div className={styles.widgetPicker} role="menu" aria-label={`${PRODUCT_NAME} widgets`}>
        <div className={styles.widgetPickerBrand}>
          <DeskPulseMark live={pathLive} />
          <div>
            <p className={styles.widgetPickerBrandName}>{PRODUCT_NAME}</p>
            <p className={styles.widgetPickerBrandTag}>{PRODUCT_TAG}</p>
          </div>
        </div>
        <p className={styles.widgetPickerLead}>
          {slots === 1 ? '1 slot' : `${slots} slots`} on <strong>{planName}</strong>
          {isFree || tier === 'none' ? ' · Operator unlocks more' : null}
        </p>
        <ul className={styles.widgetPickerList}>
          {PULSE_WIDGETS.map((w) => {
            const allowed = canUsePulseWidget(w.id, tier)
            const active = selected.includes(w.id)
            return (
              <li key={w.id}>
                <button
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={active}
                  className={`${styles.widgetPickerItem} ${active ? styles.widgetPickerItemOn : ''} ${
                    !allowed ? styles.widgetPickerItemLocked : ''
                  }`}
                  onClick={() => toggleWidget(w.id)}
                >
                  <span className={styles.widgetPickerCheck} aria-hidden>
                    {active ? '●' : allowed ? '○' : <LockOutlined />}
                  </span>
                  <span className={styles.widgetPickerCopy}>
                    <span className={styles.widgetPickerTitle}>{w.label}</span>
                    <span className={styles.widgetPickerDesc}>{w.description}</span>
                  </span>
                  {!allowed ? (
                    <span className={styles.widgetPickerUnlock}>{unlockLabel(w.minTier)}</span>
                  ) : null}
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    ),
    [isFree, pathLive, planName, selected, slots, tier, toggleWidget]
  )

  if (collapsed) {
    return (
      <aside className={`${styles.pulse} ${styles.pulseCollapsed}`} aria-label={PRODUCT_NAME}>
        <Tooltip title={`${PRODUCT_NAME} — ${PRODUCT_TAG}`} placement="right">
          <button
            type="button"
            className={styles.pulseCollapsedBtn}
            aria-label={`Open ${PRODUCT_NAME}`}
            onClick={() => router.push('/client/dashboard')}
          >
            <DeskPulseMark live={pathLive} />
          </button>
        </Tooltip>
      </aside>
    )
  }

  const showPaidTeaser = (isFree || tier === 'none') && slots === 1

  return (
    <aside
      className={styles.pulse}
      aria-label={PRODUCT_NAME}
      data-pulse-header={headerOnly ? '1' : undefined}
    >
      <header className={styles.pulseHeader}>
        <div className={styles.pulseBrand}>
          <DeskPulseMark live={pathLive} />
          <div className={styles.pulseBrandText}>
            <h2 className={styles.pulseTitle} title={`${PRODUCT_NAME} · ${PRODUCT_TAG}`}>
              Pulse
            </h2>
          </div>
        </div>
        <div className={styles.pulseHeaderActions}>
          <span
            className={styles.pulseSlots}
            title={`${selected.length} of ${slots} snapshot slots`}
          >
            {selected.length}/{slots}
          </span>
          <Popover
            content={picker}
            trigger="click"
            placement="rightTop"
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            classNames={{ root: 'sidebar-pulse-popover' }}
          >
            <button
              type="button"
              className={styles.pulseEdit}
              aria-label={`Customize ${PRODUCT_NAME}`}
              aria-expanded={pickerOpen}
            >
              ···
            </button>
          </Popover>
        </div>
      </header>

      {headerOnly ? null : (
      <div className={styles.pulseStack}>
        <CompactPulseCard
          selected={selected}
          onNavigate={(href) => router.push(href)}
        />
        {showPaidTeaser ? (
          <LockedSlot label="More snapshots" unlock="Operator+" />
        ) : null}
      </div>
      )}
    </aside>
  )
}

export default PulseWidgetHolder
